/**
 * TschauSepp Runden-Viewer
 * ========================
 * 
 * Diese Komponente zeigt den Spielstand einer geteilten Runde an.
 * 
 * Features:
 * - Read-only Ansicht für Mitspieler
 * - Host-Modus mit Bearbeitungsrechten (wenn ?key=... vorhanden)
 * - Auto-Refresh alle 5 Sekunden
 * - Link-Sharing mit Copy-Button
 * - Mobile-first Design
 * 
 * URL-Struktur:
 * - /runde/:roundId - Viewer-Ansicht
 * - /runde/:roundId?key=:hostKey - Host-Ansicht
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { 
  Trophy, Users, RefreshCw, Copy, Check, Share2, 
  Home, Shuffle, Zap, Save, Clock, Crown, ExternalLink,
  Plus, Minus, AlertCircle, Loader2, MessageCircle,
  Pencil, UserPlus, UserMinus, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Jass Cards Background (simplified version)
const JassCardsBackground = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
    <div className="absolute inset-0 bg-gradient-to-br from-[#264F77] via-[#1e3d5c] to-[#162d42]" />
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute -top-20 -left-20 w-96 h-96 border-2 border-white/10 rounded-full" />
      <div className="absolute top-40 -right-10 w-64 h-64 border-2 border-white/10 rounded-full" />
    </div>
  </div>
);

// Share Link Modal
const ShareModal = ({ isOpen, onClose, roundId, players }) => {
  const [copied, setCopied] = useState(false);
  const viewerUrl = `${window.location.origin}/runde/${roundId}`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(viewerUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareViaWhatsApp = () => {
    const playerNames = players?.map(p => p.name).join(', ') || '';
    const text = `🃏 Schau dir unser Tschau-Sepp Spiel an!\n\nSpieler: ${playerNames}\n\nLive-Spielstand: ${viewerUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div 
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-xl font-semibold text-[#264F77] mb-4 flex items-center gap-2">
          <Share2 size={24} />
          Runde teilen
        </h3>
        <p className="text-gray-600 mb-4">
          Teile diesen Link mit deinen Mitspielern:
        </p>
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={viewerUrl}
            readOnly
            className="flex-1 px-4 py-3 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700"
          />
          <button
            onClick={copyToClipboard}
            className={`px-4 py-3 rounded-lg font-medium transition-all ${
              copied 
                ? 'bg-green-500 text-white' 
                : 'bg-[#264F77] text-white hover:bg-[#1e3d5c]'
            }`}
          >
            {copied ? <Check size={20} /> : <Copy size={20} />}
          </button>
        </div>
        {/* WhatsApp Share Button */}
        <button
          onClick={shareViaWhatsApp}
          className="w-full py-3 bg-[#25D366] text-white font-medium rounded-lg hover:bg-[#20bd5a] transition-colors flex items-center justify-center gap-2 mb-4"
        >
          <MessageCircle size={20} />
          Via WhatsApp teilen
        </button>
        <button
          onClick={onClose}
          className="w-full py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
        >
          Schliessen
        </button>
      </div>
    </div>
  );
};

export const TschauSeppRunde = () => {
  const { roundId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hostKey = searchParams.get('key');
  
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Local state for editing (host only)
  const [localRoundScores, setLocalRoundScores] = useState([]);

  // Edit history state
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  // Player edit modal
  const [showPlayerEditModal, setShowPlayerEditModal] = useState(false);
  const [editPlayerNames, setEditPlayerNames] = useState([]);

  // Fetch round data
  const fetchRound = useCallback(async () => {
    try {
      const url = hostKey 
        ? `${API_URL}/api/tschausepp/rounds/${roundId}?key=${hostKey}`
        : `${API_URL}/api/tschausepp/rounds/${roundId}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Runde nicht gefunden');
        }
        throw new Error('Fehler beim Laden der Runde');
      }
      
      const data = await response.json();
      setRound(data);
      setLocalRoundScores(data.round_scores || []);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [roundId, hostKey]);

  // Initial fetch and polling
  useEffect(() => {
    fetchRound();
    
    // Poll every 5 seconds for updates (viewer mode)
    const interval = setInterval(() => {
      if (!hostKey) {
        fetchRound();
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [fetchRound, hostKey]);

  // Update round scores (host only)
  const handleScoreChange = (index, value) => {
    const newScores = [...localRoundScores];
    newScores[index] = parseInt(value) || 0;
    setLocalRoundScores(newScores);
  };

  // Save current round scores to server
  const updateScoresOnServer = async (scores) => {
    if (!hostKey) return;
    
    try {
      await fetch(`${API_URL}/api/tschausepp/rounds/${roundId}?key=${hostKey}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ round_scores: scores })
      });
    } catch (err) {
      console.error('Failed to update scores:', err);
    }
  };

  // Call double round
  const callDoubleRound = async (playerIndex) => {
    if (!hostKey) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/tschausepp/rounds/${roundId}/call-double?key=${hostKey}&player_index=${playerIndex}`,
        { method: 'POST' }
      );
      if (response.ok) {
        const data = await response.json();
        setRound(data);
      }
    } catch (err) {
      console.error('Failed to call double:', err);
    }
  };

  // Under finish
  const handleUnderFinish = async () => {
    if (!hostKey) return;
    
    try {
      // First sync local scores to server
      await updateScoresOnServer(localRoundScores);
      
      // Then call under-finish
      const response = await fetch(
        `${API_URL}/api/tschausepp/rounds/${roundId}/under-finish?key=${hostKey}`,
        { method: 'POST' }
      );
      if (response.ok) {
        const data = await response.json();
        setRound(data);
        setLocalRoundScores(data.round_scores);
      }
    } catch (err) {
      console.error('Failed to under finish:', err);
    }
  };

  // Save round
  const handleSaveRound = async () => {
    if (!hostKey) return;
    
    setSaving(true);
    try {
      // First update scores
      await updateScoresOnServer(localRoundScores);
      
      // Then save the round
      const response = await fetch(
        `${API_URL}/api/tschausepp/rounds/${roundId}/save-round?key=${hostKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            round_scores: localRoundScores,
            multiplier: round.multiplier
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setRound(data);
        setLocalRoundScores(data.round_scores);
      }
    } catch (err) {
      console.error('Failed to save round:', err);
    } finally {
      setSaving(false);
    }
  };

  // Get winner
  const getWinner = () => {
    if (!round || !round.players) return null;
    const minScore = Math.min(...round.players.map(p => p.score_total));
    return round.players.find(p => p.score_total === minScore);
  };

  // ---- EDIT HISTORY ----
  const startEditCell = (roundIdx, playerIdx) => {
    if (!isHost) return;
    const history = round?.history || [];
    const currentScore = history[roundIdx]?.scores?.[playerIdx] || 0;
    setEditingCell({ roundIdx, playerIdx });
    setEditValue(String(currentScore));
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveEditCell = async () => {
    if (!editingCell || !hostKey) return;
    const { roundIdx, playerIdx } = editingCell;
    const newScore = parseInt(editValue) || 0;

    try {
      const response = await fetch(
        `${API_URL}/api/tschausepp/rounds/${roundId}/edit-history?key=${hostKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            round_index: roundIdx,
            player_index: playerIdx,
            new_score: newScore
          })
        }
      );
      if (response.ok) {
        const data = await response.json();
        setRound(data);
      }
    } catch (err) {
      console.error('Failed to edit history:', err);
    }
    cancelEdit();
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') saveEditCell();
    if (e.key === 'Escape') cancelEdit();
  };

  // ---- NEW GAME (keep players) ----
  const startNewGameWithPlayers = async () => {
    if (!hostKey) return;
    try {
      const response = await fetch(
        `${API_URL}/api/tschausepp/rounds/${roundId}/new-game?key=${hostKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        }
      );
      if (response.ok) {
        const data = await response.json();
        navigate(`/runde/${data.id}?key=${data.host_key}`);
      }
    } catch (err) {
      console.error('Failed to create new game:', err);
    }
  };

  // ---- PLAYER EDIT ----
  const openPlayerEdit = () => {
    setEditPlayerNames(players.map(p => p.name));
    setShowPlayerEditModal(true);
  };

  const handleEditPlayerName = (index, name) => {
    const newNames = [...editPlayerNames];
    newNames[index] = name;
    setEditPlayerNames(newNames);
  };

  const addEditPlayer = () => {
    setEditPlayerNames(prev => [...prev, '']);
  };

  const removeEditPlayer = (index) => {
    if (editPlayerNames.length <= 2) return;
    setEditPlayerNames(prev => prev.filter((_, i) => i !== index));
  };

  const savePlayerEdits = async () => {
    if (!hostKey) return;
    const actions = [];
    const currentCount = players.length;
    const newCount = editPlayerNames.length;

    for (let i = 0; i < Math.min(currentCount, newCount); i++) {
      const newName = editPlayerNames[i].trim() || `Spieler ${i + 1}`;
      if (newName !== players[i].name) {
        actions.push({ action: 'rename', index: i, name: newName });
      }
    }

    if (newCount < currentCount) {
      for (let i = currentCount - 1; i >= newCount; i--) {
        actions.push({ action: 'remove', index: i });
      }
    }

    if (newCount > currentCount) {
      for (let i = currentCount; i < newCount; i++) {
        actions.push({ action: 'add', name: editPlayerNames[i].trim() || `Spieler ${i + 1}` });
      }
    }

    if (actions.length > 0) {
      try {
        const response = await fetch(
          `${API_URL}/api/tschausepp/rounds/${roundId}/edit-players?key=${hostKey}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ players: actions })
          }
        );
        if (response.ok) {
          const data = await response.json();
          setRound(data);
          setLocalRoundScores(data.round_scores || []);
        }
      } catch (err) {
        console.error('Failed to edit players:', err);
      }
    }
    setShowPlayerEditModal(false);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen relative">
        <JassCardsBackground />
        <div className="relative z-10 min-h-screen flex items-center justify-center">
          <div className="text-center text-white">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
            <p className="text-lg">Runde wird geladen...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen relative">
        <JassCardsBackground />
        <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">
              {error}
            </h2>
            <p className="text-gray-600 mb-6">
              Diese Runde existiert nicht oder wurde gelöscht.
            </p>
            <Link
              to="/tschausepp"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#264F77] text-white font-medium rounded-lg hover:bg-[#1e3d5c] transition-colors"
            >
              <Home size={18} />
              Neues Spiel starten
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isHost = round?.is_host;
  const players = round?.players || [];
  const history = round?.history || [];

  return (
    <div className="min-h-screen relative">
      <JassCardsBackground />
      <ShareModal 
        isOpen={showShareModal} 
        onClose={() => setShowShareModal(false)} 
        roundId={roundId}
        players={players}
      />

      {/* Player Edit Modal */}
      {showPlayerEditModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setShowPlayerEditModal(false)}
        >
          <div className="absolute inset-0 bg-black/60" />
          <div 
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
            data-testid="player-edit-modal-viewer"
          >
            <button
              onClick={() => setShowPlayerEditModal(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <X size={16} className="text-gray-600" />
            </button>
            <h3 className="text-xl font-semibold text-[#264F77] mb-2 flex items-center gap-2">
              <Users size={24} />
              Spieler bearbeiten
            </h3>
            <p className="text-gray-600 mb-5 text-sm">
              Spieler umbenennen, hinzufügen oder entfernen.
            </p>
            <div className="space-y-3 mb-6">
              {editPlayerNames.map((name, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="w-6 text-sm text-gray-400 text-center">{index + 1}.</span>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => handleEditPlayerName(index, e.target.value)}
                    placeholder={`Spieler ${index + 1}`}
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent outline-none transition-all"
                  />
                  {editPlayerNames.length > 2 && (
                    <button
                      onClick={() => removeEditPlayer(index)}
                      className="w-10 h-10 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                    >
                      <UserMinus size={18} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={addEditPlayer}
              className="w-full py-3 mb-4 border-2 border-dashed border-gray-300 text-gray-500 font-medium rounded-lg hover:border-[#264F77] hover:text-[#264F77] transition-colors flex items-center justify-center gap-2"
            >
              <UserPlus size={18} />
              Spieler hinzufügen
            </button>
            <div className="flex gap-2">
              <button
                onClick={() => setShowPlayerEditModal(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={savePlayerEdits}
                className="flex-1 py-3 bg-[#264F77] text-white font-medium rounded-lg hover:bg-[#1e3d5c] transition-colors"
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-6 lg:px-8 pt-6">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <Link 
              to="/tschausepp" 
              className="flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-colors"
            >
              <Home size={18} />
              <span className="text-sm font-medium hidden sm:inline">Neues Spiel</span>
            </Link>
            
            <div className="flex items-center gap-2">
              {/* Share Button */}
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-colors"
              >
                <Share2 size={18} />
                <span className="text-sm font-medium hidden sm:inline">Teilen</span>
              </button>
              
              {/* Player Edit Button (host only) */}
              {isHost && (
                <button
                  onClick={openPlayerEdit}
                  className="flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-colors"
                  data-testid="edit-players-viewer-btn"
                >
                  <Pencil size={18} />
                  <span className="text-sm font-medium hidden sm:inline">Spieler</span>
                </button>
              )}

              {/* Refresh Button (viewer only) */}
              {!isHost && (
                <button
                  onClick={fetchRound}
                  className="flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  <RefreshCw size={18} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full">
          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Tschau-Sepp
            </h1>
            <div className="flex items-center justify-center gap-3 text-white/70 text-sm">
              <span className={`px-3 py-1 rounded-full ${isHost ? 'bg-green-500/30 text-green-200' : 'bg-white/20'}`}>
                {isHost ? '👑 Host' : '👁️ Zuschauer'}
              </span>
              {lastUpdated && (
                <span className="flex items-center gap-1">
                  <Clock size={14} />
                  {lastUpdated.toLocaleTimeString('de-CH')}
                </span>
              )}
            </div>
          </div>

          {round?.status === 'ended' ? (
            /* Game Over Screen */
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6 md:p-8 text-center">
              <Trophy className="mx-auto text-yellow-500 mb-4" size={64} />
              <h2 className="text-3xl font-bold text-[#264F77] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Spiel beendet!
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                Gewinner: <span className="font-bold text-[#264F77]">{getWinner()?.name}</span>
              </p>
              
              {/* Final Scores */}
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-[#264F77] mb-4">Endstand</h3>
                <div className="space-y-2">
                  {players
                    .slice()
                    .sort((a, b) => a.score_total - b.score_total)
                    .map((player, index) => (
                    <div key={player.id} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-0">
                      <span className="font-medium flex items-center gap-2">
                        {index === 0 && <Crown className="text-yellow-500" size={18} />}
                        {player.name}
                      </span>
                      <span className={`font-bold ${index === 0 ? 'text-green-600' : 'text-gray-700'}`}>
                        {player.score_total} Punkte
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Game History with editable cells (host only) */}
              {history.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-6 mb-6 text-left">
                  <h3 className="text-lg font-semibold text-[#264F77] mb-4">Rundenverlauf</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 text-gray-600 text-sm">Runde</th>
                          {players.map((player) => (
                            <th key={player.id} className="text-center py-2 px-3 text-gray-600 text-sm">{player.name}</th>
                          ))}
                          <th className="text-center py-2 px-3 text-gray-600 text-sm">Multi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((entry, i) => (
                          <tr key={i} className={`border-b border-gray-100 ${entry.edited ? 'bg-amber-50' : ''}`}>
                            <td className="py-2 px-3 text-gray-700 flex items-center gap-1">
                              {entry.round_number}
                              {entry.edited && <Pencil size={12} className="text-amber-500" />}
                            </td>
                            {entry.scores.map((score, j) => (
                              <td key={j} className="text-center py-2 px-3">
                                {isHost && editingCell?.roundIdx === i && editingCell?.playerIdx === j ? (
                                  <input
                                    type="number"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={saveEditCell}
                                    onKeyDown={handleEditKeyDown}
                                    className="w-16 px-2 py-1 border-2 border-[#264F77] rounded text-center font-medium focus:outline-none"
                                    autoFocus
                                  />
                                ) : isHost ? (
                                  <button
                                    onClick={() => startEditCell(i, j)}
                                    className="w-full font-medium hover:bg-[#264F77]/10 rounded px-2 py-1 transition-colors cursor-pointer"
                                    title="Klicken zum Bearbeiten"
                                  >
                                    {score}
                                  </button>
                                ) : (
                                  <span className="font-medium">{score}</span>
                                )}
                              </td>
                            ))}
                            <td className="text-center py-2 px-3 text-[#264F77]">{entry.multiplier}x</td>
                          </tr>
                        ))}
                        <tr className="bg-gray-100 font-bold">
                          <td className="py-3 px-3 text-[#264F77]">Total</td>
                          {players.map((player) => (
                            <td key={player.id} className="text-center py-3 px-3 text-[#264F77]">
                              {player.score_total}
                            </td>
                          ))}
                          <td className="text-center py-3 px-3">-</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {isHost ? (
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={startNewGameWithPlayers}
                    className="flex-1 py-4 bg-[#264F77] text-white font-semibold rounded-lg hover:bg-[#1e3d5c] transition-colors shadow-lg flex items-center justify-center gap-2"
                    data-testid="new-game-viewer-btn"
                  >
                    <RefreshCw size={20} />
                    Neues Spiel starten
                  </button>
                  <button
                    onClick={openPlayerEdit}
                    className="flex-1 py-4 bg-white border-2 border-[#264F77] text-[#264F77] font-semibold rounded-lg hover:bg-[#264F77]/5 transition-colors flex items-center justify-center gap-2"
                  >
                    <Users size={20} />
                    Spieler bearbeiten
                  </button>
                </div>
              ) : (
                <Link
                  to="/tschausepp"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#264F77] text-white font-semibold rounded-lg hover:bg-[#1e3d5c] transition-colors shadow-lg"
                >
                  Neues Spiel starten
                </Link>
              )}
            </div>
          ) : (
            /* Active Game */
            <div className="space-y-6">
              {/* Round Info */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-lg font-semibold text-white drop-shadow">
                  Runde {round?.current_round} von {round?.total_rounds}
                </h2>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-white/20 text-white text-sm rounded-full flex items-center space-x-1">
                    <Shuffle size={14} />
                    <span>{players[round?.current_player_index]?.name}</span>
                  </span>
                  <span className="px-3 py-1 bg-[#1e3d5c] text-white text-sm rounded-full">
                    {round?.multiplier}x
                  </span>
                </div>
              </div>

              {/* Score Card */}
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6">
                <h3 className="font-semibold text-gray-700 mb-4">
                  {isHost ? 'Punkte eingeben' : 'Aktuelle Runde'}
                </h3>

                {/* Host Controls */}
                {isHost && (
                  <>
                    {/* Double Round Buttons */}
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2">Doppelrunden-Ansagen:</p>
                      <div className="flex flex-wrap gap-2">
                        {players.map((player, index) => (
                          <button
                            key={player.id}
                            onClick={() => callDoubleRound(index)}
                            disabled={player.double_rounds_used >= round?.double_rounds_per_player}
                            className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-all ${
                              player.double_rounds_used >= round?.double_rounds_per_player
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-[#264F77] text-white hover:bg-[#1e3d5c]'
                            }`}
                          >
                            {player.name} ({round?.double_rounds_per_player - player.double_rounds_used})
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Under-Finish Button */}
                    <button
                      onClick={handleUnderFinish}
                      className="w-full py-3 bg-gradient-to-r from-[#264F77] to-[#1e3d5c] text-white font-medium rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center space-x-2 mb-6"
                    >
                      <Zap size={18} />
                      <span>Under-Finish</span>
                    </button>
                  </>
                )}

                {/* Player Scores */}
                <div className="space-y-4">
                  {players.map((player, index) => (
                    <div key={player.id} className="flex items-center space-x-4">
                      <span className="w-28 font-medium text-gray-700 truncate">{player.name}</span>
                      {isHost ? (
                        <input
                          type="number"
                          value={localRoundScores[index] || 0}
                          onChange={(e) => handleScoreChange(index, e.target.value)}
                          className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent outline-none text-center font-semibold"
                          min="0"
                        />
                      ) : (
                        <div className="flex-1 px-4 py-3 bg-gray-100 rounded-lg text-center font-semibold text-gray-700">
                          {round?.round_scores?.[index] || 0}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Save Round Button (Host only) */}
                {isHost && (
                  <button
                    onClick={handleSaveRound}
                    disabled={saving}
                    className="w-full mt-6 py-4 bg-[#264F77] text-white font-semibold rounded-lg hover:bg-[#1e3d5c] transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                  >
                    {saving ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <Save size={18} />
                    )}
                    <span>{saving ? 'Speichern...' : 'RUNDE SPEICHERN'}</span>
                  </button>
                )}
              </div>

              {/* Score Board */}
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6">
                <h3 className="text-lg font-semibold text-[#264F77] mb-4">Spielstand</h3>
                
                {history.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Noch keine Runden gespielt</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 text-gray-600 text-sm">Runde</th>
                          {players.map((player) => (
                            <th key={player.id} className="text-center py-2 px-3 text-gray-600 text-sm">
                              {player.name}
                            </th>
                          ))}
                          <th className="text-center py-2 px-3 text-gray-600 text-sm">Multi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((entry, i) => (
                          <tr key={i} className={`border-b border-gray-100 ${entry.edited ? 'bg-amber-50' : ''}`}>
                            <td className="py-2 px-3 text-gray-700 flex items-center gap-1">
                              {entry.round_number}
                              {entry.edited && <Pencil size={12} className="text-amber-500" />}
                            </td>
                            {entry.scores.map((score, j) => (
                              <td key={j} className="text-center py-2 px-3">
                                {isHost && editingCell?.roundIdx === i && editingCell?.playerIdx === j ? (
                                  <input
                                    type="number"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={saveEditCell}
                                    onKeyDown={handleEditKeyDown}
                                    className="w-16 px-2 py-1 border-2 border-[#264F77] rounded text-center font-medium focus:outline-none"
                                    autoFocus
                                  />
                                ) : isHost ? (
                                  <button
                                    onClick={() => startEditCell(i, j)}
                                    className="w-full font-medium hover:bg-[#264F77]/10 rounded px-2 py-1 transition-colors cursor-pointer"
                                    title="Klicken zum Bearbeiten"
                                  >
                                    {score}
                                  </button>
                                ) : (
                                  <span className="font-medium">{score}</span>
                                )}
                              </td>
                            ))}
                            <td className="text-center py-2 px-3 text-[#264F77]">{entry.multiplier}x</td>
                          </tr>
                        ))}
                        <tr className="bg-gray-50 font-bold">
                          <td className="py-3 px-3 text-[#264F77]">Total</td>
                          {players.map((player) => (
                            <td key={player.id} className="text-center py-3 px-3 text-[#264F77]">
                              {player.score_total}
                            </td>
                          ))}
                          <td className="text-center py-3 px-3">-</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className="py-4 text-center text-white/60 text-sm">
          <p>LIVAG Tschau-Sepp Rechner</p>
        </footer>
      </div>
    </div>
  );
};

export default TschauSeppRunde;
